﻿import React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/system";
import PropTypes from "prop-types";

const sizes = {
  large: {
    height: "55px",
    minWidth: "90px",
    padding: "0 8px",
    borderRadius: "7px",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      fontSize: "18px",
      margin: "0 4px",
    },
  },
  medium: {
    height: "50px",
    minWidth: "82px",
    padding: "0 8px",
    borderRadius: "6px",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      fontSize: "16px",
      margin: "0 4px",
    },
  },
  small: {
    height: "40px",
    minWidth: "50px",
    padding: "0 4px",
    borderRadius: "4px",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      fontSize: "14px",
      margin: "0 4px",
    },
  },
};

const primaryStyles = {
  default: {
    backgroundColor: "var(--primary-main)",
    color: "var(--default-light)",
    border: "1.5px solid transparent",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--default-light)",
      "& path": {
        fill: "var(--default-light)",
      },
    },
  },
  hover: {
    backgroundColor: "var(--status-upcoming-intense)",
    color: "var(--primary-main)",
    border: "2px solid var(--primary-main)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--primary-main)",
      "& path": {
        fill: "var(--primary-main)",
      },
    },
  },
  active: {
    backgroundColor: "var(--status-upcoming-intense)",
    color: "var(--primary-main)",
    border: "2px solid var(--primary-main)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--primary-main)",
      "& path": {
        fill: "var(--primary-main)",
      },
    },
  },
  disabled: {
    backgroundColor: "var(--grey-lowlight) !important",
    color: "var(--default-light)",
    border: "2px solid transparent",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--default-light)",
      "& path": {
        fill: "var(--default-light)",
      },
    },
  },
};

const secondaryStyles = {
  default: {
    backgroundColor: "var(--default-light)",
    color: "var(--primary-main)",
    border: "1.5px solid var(--primary-main)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--primary-main)",
      "& path": {
        fill: "var(--primary-main)",
      },
    },
  },
  hover: {
    backgroundColor: "var(--status-upcoming-intense)",
    color: "var(--primary-main)",
    border: "2px solid var(--primary-main)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--primary-main)",
      "& path": {
        fill: "var(--primary-main)",
      },
    },
  },
  active: {
    backgroundColor: "var(--status-upcoming-intense)",
    color: "var(--primary-main)",
    border: "2px solid var(--primary-main)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--primary-main)",
      "& path": {
        fill: "var(--primary-main)",
      },
    },
  },
  disabled: {
    backgroundColor: "var(--grey-lowlight) !important",
    color: "var(--default-light)",
    border: "2px solid transparent",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      color: "var(--default-light)",
      "& path": {
        fill: "var(--default-light)",
      },
    },
  },
};

const getButtonStyles = (variant) => {
  return variant === "secondary" ? secondaryStyles : primaryStyles;
};

const StyledButton = styled(Button)(({ size, variant }) => {
  const styles = getButtonStyles(variant);
  return {
    boxSizing: "border-box",
    ...sizes[size],
    textTransform: "none",
    fontFamily: "var(--font-family)",
    fontSize: "20px",
    ...styles.default,
    "&:hover": styles.hover,
    "&:active": styles.active,
    "&.Mui-disabled": styles.disabled,
    "&.disabled": styles.disabled,
    ".dialog-actions &": {
      ...sizes[size],
      ...styles.default,
      "&:hover": styles.hover,
      "&:active": styles.active,
      "&.Mui-disabled": styles.disabled,
      "&.disabled": styles.disabled,
    },
    whiteSpace: "nowrap",
    [`&.MuiButton-root`]: {
      padding: "0px 25px",
    },
  };
});

const ShepButton = ({
  size,
  variant,
  icon,
  iconPosition,
  children,
  ...props
}) => {
  return (
    <StyledButton
      size={size}
      variant={variant}
      startIcon={icon && iconPosition === "left" ? icon : null}
      endIcon={icon && iconPosition === "right" ? icon : null}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

ShepButton.propTypes = {
  size: PropTypes.oneOf(["large", "medium", "small"]),
  variant: PropTypes.oneOf(["primary", "secondary"]),
  icon: PropTypes.element,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  children: PropTypes.node.isRequired,
};

ShepButton.defaultProps = {
  size: "medium",
  variant: "primary",
  icon: null,
  iconPosition: "left",
};

export default ShepButton;
